import type { Campaign } from '../types'
import {
  hasPersistedLinesOfInquiry,
  isCampaignLaunched,
} from './campaignResumeWizardPath'

/** Campaign wizard routes that use Dashboard-style AppShell layout + back button. */
export const CAMPAIGN_WIZARD_PATH_RE =
  /^\/campaigns\/([^/]+)\/(setup|goal-analysis|lines-of-inquiry|team-and-launch)\/?$/

const CAMPAIGN_WIZARD_STEPS = [
  'setup',
  'goal-analysis',
  'lines-of-inquiry',
  'team-and-launch',
] as const

export type CampaignWizardStep = (typeof CAMPAIGN_WIZARD_STEPS)[number]

function isCampaignWizardStep(value: string): value is CampaignWizardStep {
  return (CAMPAIGN_WIZARD_STEPS as readonly string[]).includes(value)
}

export function parseCampaignWizardPath(pathname: string): {
  campaignId: string
  step: CampaignWizardStep
} | null {
  const m = CAMPAIGN_WIZARD_PATH_RE.exec(pathname)
  if (!m || !isCampaignWizardStep(m[2])) return null
  return {
    campaignId: m[1],
    step: m[2],
  }
}

export function isCampaignWizardPath(pathname: string): boolean {
  return CAMPAIGN_WIZARD_PATH_RE.test(pathname)
}

/**
 * AppShell back target — mirrors wizard step order (Cancel on setup still uses dashboard via dialog).
 * `new` campaign → campaigns list; setup (persisted id) → campaign detail; later steps → previous phase.
 */
export function campaignWizardBackPath(pathname: string, campaignId: string): string {
  if (!campaignId || campaignId === 'new') {
    return '/campaigns'
  }
  const parsed = parseCampaignWizardPath(pathname)
  if (!parsed) {
    return '/'
  }
  switch (parsed.step) {
    case 'team-and-launch':
      return `/campaigns/${campaignId}/lines-of-inquiry`
    case 'lines-of-inquiry':
      return `/campaigns/${campaignId}/goal-analysis`
    case 'goal-analysis':
      return `/campaigns/${campaignId}/setup`
    case 'setup':
    default:
      return `/campaigns/${campaignId}`
  }
}

export const WIZARD_CONTINUE_TO_TEAM_STATE = 'wizardContinueToTeam' as const

export type WizardContinueToTeamLocationState = {
  readonly [WIZARD_CONTINUE_TO_TEAM_STATE]?: boolean
  readonly campaign?: Campaign
}

export function readWizardContinueToTeamState(
  state: unknown,
): WizardContinueToTeamLocationState | null {
  if (state == null || typeof state !== 'object') return null
  const record = state as Record<string, unknown>
  return {
    [WIZARD_CONTINUE_TO_TEAM_STATE]:
      record[WIZARD_CONTINUE_TO_TEAM_STATE] === true ? true : undefined,
    campaign:
      record.campaign != null && typeof record.campaign === 'object'
        ? (record.campaign as Campaign)
        : undefined,
  }
}

/** Pin Team & Launch UI until GET confirms wizard step 4 (avoids redirect on stale cache). */
export function shouldHoldWizardContinueOnTeam(
  pinned: Campaign | undefined,
  server: Campaign | undefined,
  fromWizardContinue: boolean,
): boolean {
  if (!fromWizardContinue || !pinned) return false
  if (isCampaignLaunched(pinned)) return false
  if (pinned.wizardStep < 4 || !hasPersistedLinesOfInquiry(pinned)) {
    return false
  }
  if (
    server &&
    server.wizardStep >= 4 &&
    !isCampaignLaunched(server)
  ) {
    return false
  }
  return true
}

export function isServerTeamWizardReady(campaign: Campaign | undefined): boolean {
  return Boolean(
    campaign &&
      campaign.wizardStep >= 4 &&
      !isCampaignLaunched(campaign),
  )
}

/**
 * True when browser Back from Team would leave this campaign's wizard
 * (e.g. tenant home) because earlier steps used `replace`. US187 expects
 * Back to land on Lines of Inquiry instead.
 */
export function shouldInterceptTeamWizardBrowserBack(
  historyAction: string,
  nextPathname: string,
  campaignId: string,
): boolean {
  if (historyAction !== 'POP') return false
  if (!campaignId || campaignId === 'new') return false
  const next = parseCampaignWizardPath(nextPathname)
  if (next?.campaignId === campaignId) return false
  return true
}
